import React, { useState } from "react";
import { dateFormat } from "src/utils";
import styled from "styled-components";

interface RoundDateSelectorProps {
  selectedRound: any;
  rounds: any[];
  onDateSelect: (roundId: number) => void;
  className?: string;
}

const RoundDateSelector: React.FC<RoundDateSelectorProps> = ({
  selectedRound,
  rounds,
  onDateSelect,
  className,
}) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const handleDateSelect = (roundId: number) => {
    onDateSelect(roundId);
    setIsPopupOpen(false);
  };

  return (
    <Container className={className}>
      <div className="round-date-label">라운드 일자</div>
      <div className="date-input" onClick={() => setIsPopupOpen(true)}>
        <div className="date-text">
          <span className="date-main">
            {selectedRound
              ? `${dateFormat(new Date(selectedRound.round_date))}  - ${
                  (rounds?.findIndex((r) => r.id === selectedRound.id) ?? 0) + 1
                }회차`
              : "라운드 일자를 선택하세요"}
          </span>
          <span className="payment-status">
            {selectedRound?.is_paid ? "(결제완료)" : "(결제미납)"}
          </span>
        </div>
        <div className="dropdown-icon">
          <svg width="10" height="5" viewBox="0 0 10 5" fill="none">
            <path
              d="M9.41675 0.416667L4.83341 5L0.250081 0.416666L9.41675 0.416667Z"
              fill="#1A1A1A"
            />
          </svg>
        </div>
      </div>

      {/* Date Selection Popup */}
      {isPopupOpen && (
        <DatePopup>
          <PopupOverlay onClick={() => setIsPopupOpen(false)} />
          <PopupContent>
            <PopupHeader>
              <PopupTitle>라운드 일자 선택</PopupTitle>
              <CloseButton onClick={() => setIsPopupOpen(false)}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="#1A1A1A"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </CloseButton>
            </PopupHeader>
            <PopupBody>
              {rounds?.map((round, index) => (
                <DateOption
                  key={round.id}
                  onClick={() => handleDateSelect(round.id)}
                  className={selectedRound?.id === round.id ? "selected" : ""}
                >
                  <div className="date-info">
                    <span className="date-text">
                      {dateFormat(new Date(round.round_date))} 
                    </span>
                    <span className="round-number">{index + 1}회차</span>
                  </div>
                  <div className="payment-status">
                    {round.is_paid ? "(결제완료)" : "(결제미납)"}
                  </div>
                </DateOption>
              ))}
            </PopupBody>
          </PopupContent>
        </DatePopup>
      )}
    </Container>
  );
};

const Container = styled.div`
  /* Round Date Section */
  .round-date-label {
    color: var(--teeshot-black, #1a1a1a);
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
    margin-bottom: 8px;
  }

  .date-input {
    display: flex;
    width: 100%;
    height: 44px;
    padding: 12px 10px;
    justify-content: space-between;
    align-items: center;
    border-radius: 6px;
    border: 1px solid var(--teeshot-g2, #d1d5d8);
    background: var(--teeshot-white, #fdfdfd);
    cursor: pointer;
    margin-bottom: 16px;
  }

  .date-text {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex: 1;
  }

  .date-main {
    color: var(--teeshot-black, #1a1a1a);
    font-weight: 700;
    font-size: 14px;
  }

  .payment-status {
    color: var(--teeshot-negative, #ff4949);
    font-weight: 700;
    font-size: 12px;
  }

  .dropdown-icon {
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
`;

const DatePopup = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PopupOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
`;

const PopupContent = styled.div`
  position: relative;
  width: 90%;
  max-width: 400px;
  max-height: 80vh;
  background: var(--teeshot-white, #fdfdfd);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
`;

const PopupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--teeshot-g2, #d1d5d8);
`;

const PopupTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--teeshot-black, #1a1a1a);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: var(--teeshot-g3, #f0f0f4);
  }
`;

const PopupBody = styled.div`
  padding: 16px 0;
  max-height: 60vh;
  overflow-y: auto;
`;

const DateOption = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: var(--teeshot-g3, #f0f0f4);
  }
  
  &.selected {
    background: var(--teeshot-green, #004b49);
    color: white;
    
    .date-text, .round-number, .payment-status {
      color: white;
    }
  }
  
  .date-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  
  .date-text {
    font-size: 16px;
    font-weight: 600;
    color: var(--teeshot-black, #1a1a1a);
  }
  
  .round-number {
    font-size: 14px;
    font-weight: 500;
    color: var(--teeshot-g1, #79818a);
  }
  
  .payment-status {
    font-size: 12px;
    font-weight: 600;
    color: var(--teeshot-negative, #ff4949);
  }
`;

export default RoundDateSelector;
